import { useEffect, useState } from "react";
import {
  Camera, CheckCircle2, ChevronRight, Download,
  FileDown, HardDrive, Info, LayoutGrid, Layers,
  Palette, Save, ScanLine, Settings as SettingsIcon,
  ShieldCheck, Smartphone, Sparkles, Trash2, Volume2, Zap,
} from "lucide-react";
import { clearStore, estimateStorage } from "../lib/indexedDb";
import { clearAppCaches } from "../lib/pwa";

/* ══════════════════════════════════════════════════════════════════════════
   Primitives
   ══════════════════════════════════════════════════════════════════════════ */

/* Colored icon badge */
function IconBadge({ Icon, color, bg }) {
  return (
    <span className="stp-ibadge" style={{ background: bg, outline: `1px solid ${color}30` }}>
      <Icon size={15} style={{ color }} />
    </span>
  );
}

/* Premium toggle */
function Toggle({ checked, onChange }) {
  return (
    <label className="stp-toggle" onClick={(e) => e.stopPropagation()}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="stp-track">
        <span className="stp-knob" />
      </span>
    </label>
  );
}

/* Select */
function Sel({ value, onChange, children }) {
  return (
    <select className="stp-select" value={value} onChange={(e) => onChange(e.target.value)}>
      {children}
    </select>
  );
}

/* Section wrapper */
function Section({ accentColor, emoji, label, badge, children }) {
  return (
    <div className="stp-section">
      <div className="stp-section-label">
        <span className="stp-section-bar" style={{ background: accentColor }} />
        <span className="stp-section-emoji">{emoji}</span>
        <span className="stp-section-text">{label}</span>
        {badge && (
          <span
            className="stp-section-badge"
            style={{ color: accentColor, background: `${accentColor}18`, borderColor: `${accentColor}38` }}
          >
            {badge}
          </span>
        )}
      </div>
      <div className="stp-card">{children}</div>
    </div>
  );
}

/* Standard row */
function Row({ Icon, iconColor, iconBg, title, sub, control }) {
  return (
    <div className="stp-row">
      <IconBadge Icon={Icon} color={iconColor} bg={iconBg} />
      <div className="stp-row-body">
        <strong>{title}</strong>
        {sub && <span>{sub}</span>}
      </div>
      {control && <div className="stp-row-end">{control}</div>}
    </div>
  );
}

/* Nav row (chevron) */
function NavRow({ Icon, iconColor, iconBg, title, sub, onClick }) {
  return (
    <button type="button" className="stp-row stp-nav-row" onClick={onClick}>
      <IconBadge Icon={Icon} color={iconColor} bg={iconBg} />
      <div className="stp-row-body">
        <strong>{title}</strong>
        {sub && <span>{sub}</span>}
      </div>
      <ChevronRight size={15} className="stp-chevron" />
    </button>
  );
}

/* Action row (tap to fire) */
function ActionRow({ Icon, iconColor, iconBg, title, sub, onClick, danger }) {
  return (
    <button
      type="button"
      className={`stp-row stp-action-row${danger ? " danger" : ""}`}
      onClick={onClick}
    >
      <IconBadge
        Icon={Icon}
        color={danger ? "#ff6b6b" : iconColor}
        bg={danger ? "rgba(255,107,107,.13)" : iconBg}
      />
      <div className="stp-row-body">
        <strong style={danger ? { color: "#ff6b6b" } : undefined}>{title}</strong>
        {sub && <span>{sub}</span>}
      </div>
      <ChevronRight size={14} style={{ color: danger ? "#ff6b6b" : "var(--muted)", opacity: .6, flexShrink: 0 }} />
    </button>
  );
}

/* ── Storage visualisation ─────────────────────────────────────────────────── */
function StorageVisual({ storage }) {
  if (!storage) {
    return <p className="stp-storage-loading">Calculating storage…</p>;
  }
  const usedMB  = Math.round((storage.usage || 0) / 1024 / 1024);
  const totalMB = Math.round((storage.quota  || 0) / 1024 / 1024);
  const pct     = totalMB > 0 ? Math.min(100, (usedMB / totalMB) * 100) : 0;
  const color   = pct > 80 ? "#ff6b6b" : pct > 55 ? "#f4c95d" : "#42f6d7";

  /* SVG donut arc */
  const R = 28, stroke = 5, circ = 2 * Math.PI * R;
  const dash = (pct / 100) * circ;

  return (
    <div className="stp-storage-wrap">
      <div className="stp-storage-ring-col">
        <svg viewBox="0 0 72 72" className="stp-storage-ring" aria-hidden="true">
          <circle cx="36" cy="36" r={R} fill="none" stroke="rgba(255,255,255,.07)" strokeWidth={stroke} />
          <circle
            cx="36" cy="36" r={R}
            fill="none"
            stroke={color}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${dash} ${circ}`}
            transform="rotate(-90 36 36)"
            style={{ filter: `drop-shadow(0 0 5px ${color}80)`, transition: "stroke-dasharray .7s cubic-bezier(.16,1,.3,1)" }}
          />
          <text x="36" y="36" textAnchor="middle" dominantBaseline="central"
            style={{ fontSize: 11, fontWeight: 800, fill: color, letterSpacing: "-.5px" }}>
            {Math.round(pct)}%
          </text>
        </svg>
      </div>
      <div className="stp-storage-info">
        <div className="stp-storage-row-pair">
          <div className="stp-storage-stat">
            <strong style={{ color }}>{usedMB} MB</strong>
            <span>used</span>
          </div>
          <div className="stp-storage-divider" />
          <div className="stp-storage-stat">
            <strong>{totalMB} MB</strong>
            <span>total</span>
          </div>
        </div>
        <div className="stp-storage-bar-track">
          <div
            className="stp-storage-bar-fill"
            style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${color}99, ${color})` }}
          />
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   Main component
   ══════════════════════════════════════════════════════════════════════════ */
export default function Settings({ settings, onChange, onThemeCenter }) {
  const [storage, setStorage]       = useState(null);
  const [installPrompt, setInstall] = useState(null);
  const [cleared, setCleared]       = useState("");

  useEffect(() => {
    estimateStorage().then(setStorage);
    const handler = (e) => { e.preventDefault(); setInstall(e); };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const set = (patch) => onChange(patch);

  const clearWith = async (label, fn) => {
    await fn();
    setCleared(label);
    estimateStorage().then(setStorage);
    setTimeout(() => setCleared(""), 2400);
  };

  /* Icon accent palette */
  const P = {
    blue:   { iconColor: "#4da3ff", iconBg: "rgba(77,163,255,.13)" },
    teal:   { iconColor: "#42f6d7", iconBg: "rgba(66,246,215,.13)" },
    purple: { iconColor: "#a78bfa", iconBg: "rgba(167,139,250,.13)" },
    orange: { iconColor: "#ff9940", iconBg: "rgba(255,153,64,.13)" },
    green:  { iconColor: "#70e087", iconBg: "rgba(112,224,135,.13)" },
    gold:   { iconColor: "#f4c95d", iconBg: "rgba(244,201,93,.13)" },
    muted:  { iconColor: "#8a9aab", iconBg: "rgba(138,154,171,.10)" },
  };

  return (
    <section className="panel-page stp-page">

      {/* ── Hero header ── */}
      <header className="stp-hero safe-top">
        <div className="stp-hero-glow" aria-hidden="true" />
        <div className="stp-hero-left">
          <div className="stp-hero-orb">
            <SettingsIcon size={26} />
          </div>
          <div>
            <h1 className="stp-hero-title">Settings</h1>
            <p className="stp-hero-sub">LensPro AI Camera</p>
          </div>
        </div>
        <div className="stp-hero-right">
          <span className="stp-hero-pill privacy">
            <ShieldCheck size={9} /> Privacy-first
          </span>
          <span className="stp-hero-pill version">v1.0.0</span>
        </div>
      </header>

      {/* ── Status chips ── */}
      <div className="stp-status-strip">
        <span className="stp-status-chip"><CheckCircle2 size={10} /> Camera</span>
        <span className="stp-status-chip"><CheckCircle2 size={10} /> Scanner</span>
        <span className="stp-status-chip"><CheckCircle2 size={10} /> Haptics</span>
      </div>

      {/* ── Toast ── */}
      {cleared && <div className="stp-toast">✓ {cleared} cleared</div>}

      {/* ── Camera ── */}
      <Section accentColor="#4da3ff" emoji="📷" label="Camera">
        <Row {...P.blue} Icon={Camera}
          title="Default camera" sub="Which lens opens first"
          control={
            <Sel value={settings.defaultCamera} onChange={(v) => set({ defaultCamera: v })}>
              <option value="environment">Rear</option>
              <option value="user">Front</option>
            </Sel>
          }
        />
        <Row {...P.blue} Icon={LayoutGrid}
          title="Grid overlay" sub="Rule-of-thirds guide lines"
          control={<Toggle checked={settings.grid} onChange={(v) => set({ grid: v })} />}
        />
        <Row {...P.blue} Icon={Layers}
          title="Mirror selfie" sub="Flip front camera horizontally"
          control={<Toggle checked={settings.mirrorSelfie} onChange={(v) => set({ mirrorSelfie: v })} />}
        />
        <Row {...P.blue} Icon={FileDown}
          title="Default export format" sub="Format used when saving photos"
          control={
            <Sel value={settings.exportFormat} onChange={(v) => set({ exportFormat: v })}>
              <option value="jpg">JPG</option>
              <option value="png">PNG</option>
              <option value="pdf">PDF</option>
            </Sel>
          }
        />
        <Row {...P.blue} Icon={Save}
          title="Save originals" sub="Keep the unedited capture"
          control={<Toggle checked={settings.saveOriginal} onChange={(v) => set({ saveOriginal: v })} />}
        />
        <Row {...P.blue} Icon={Save}
          title="Save edited" sub="Keep the edited version too"
          control={<Toggle checked={settings.saveEdited} onChange={(v) => set({ saveEdited: v })} />}
        />
        <Row {...P.blue} Icon={Smartphone}
          title="Save to device gallery" sub="Auto-download every photo & video to your phone"
          control={<Toggle checked={settings.saveToDevice !== false} onChange={(v) => set({ saveToDevice: v })} />}
        />
        <Row {...P.blue} Icon={Sparkles}
          title="Photo Polish" sub="Subtle shadow lift, crispness boost — colours stay natural"
          control={<Toggle checked={settings.autoEnhance !== false} onChange={(v) => set({ autoEnhance: v })} />}
        />
      </Section>

      {/* ── Scanner ── */}
      <Section accentColor="#42f6d7" emoji="🔍" label="Scanner" badge="Live">
        <Row {...P.teal} Icon={Zap}
          title="Auto-scan" sub="Continuously scan in QR / Barcode modes"
          control={<Toggle checked={settings.autoScan} onChange={(v) => set({ autoScan: v })} />}
        />
        <Row {...P.teal} Icon={ScanLine}
          title="Default scan mode" sub="Mode opened on first launch"
          control={
            <Sel value={settings.defaultScanMode} onChange={(v) => set({ defaultScanMode: v })}>
              <option value="qr">QR Code</option>
              <option value="barcode">Barcode</option>
              <option value="receipt">Receipt</option>
              <option value="document">Document</option>
              <option value="text">OCR / Text</option>
              <option value="object">Object ID</option>
            </Sel>
          }
        />
      </Section>

      {/* ── Appearance ── */}
      <Section accentColor="#a78bfa" emoji="🎨" label="Appearance">
        <NavRow {...P.purple} Icon={Palette}
          title="Theme Center" sub="Colors, accent, and dark mode"
          onClick={onThemeCenter}
        />
        <Row {...P.purple} Icon={Sparkles}
          title="Motion & animation" sub="Transitions and effects"
          control={
            <Sel value={settings.motion} onChange={(v) => set({ motion: v })}>
              <option value="full">Full</option>
              <option value="reduced">Reduced</option>
              <option value="none">None</option>
            </Sel>
          }
        />
      </Section>

      {/* ── Sound & Haptics ── */}
      <Section accentColor="#ff9940" emoji="🎵" label="Sound & Haptics" badge="Live">
        <Row {...P.orange} Icon={Volume2}
          title="Button sounds" sub="Clicks, shutter, and beeps"
          control={<Toggle checked={settings.soundEffects} onChange={(v) => set({ soundEffects: v })} />}
        />
        <Row {...P.orange} Icon={Smartphone}
          title="Haptic feedback"
          sub={typeof navigator !== "undefined" && !navigator.vibrate
            ? "Vibration on key actions (not supported on iOS Safari)"
            : "Vibration on key actions"}
          control={<Toggle checked={settings.hapticFeedback} onChange={(v) => set({ hapticFeedback: v })} />}
        />
      </Section>

      {/* ── Storage ── */}
      <Section accentColor="#f4c95d" emoji="💾" label="Storage">
        {/* Storage visual */}
        <div className="stp-row stp-storage-card-row">
          <HardDrive size={15} style={{ color: "#f4c95d", flexShrink: 0 }} />
          <div className="stp-row-body">
            <strong style={{ fontSize: 13, marginBottom: 6, display: "block" }}>Device storage</strong>
            <StorageVisual storage={storage} />
          </div>
        </div>
        <ActionRow {...P.muted} Icon={Trash2}
          title="Clear gallery & scan history" sub="Removes all saved photos and scans"
          danger
          onClick={() => clearWith("Gallery & scans", async () => {
            await clearStore("photos");
            await clearStore("scans");
          })}
        />
        <ActionRow {...P.muted} Icon={Trash2}
          title="Clear AI results" sub="Removes all saved AI outputs"
          danger
          onClick={() => clearWith("AI results", () => clearStore("aiResults"))}
        />
        <ActionRow {...P.muted} Icon={Trash2}
          title="Clear app cache" sub="Forces fresh assets on next load"
          onClick={() => clearWith("App cache", clearAppCaches)}
        />
      </Section>

      {/* ── Install ── */}
      {installPrompt && (
        <Section accentColor="#42f6d7" emoji="📲" label="Install">
          <ActionRow {...P.teal} Icon={Download}
            title="Install LensPro as app"
            sub="Add to home screen for the best experience"
            onClick={() => installPrompt.prompt()}
          />
        </Section>
      )}

      {/* ── About ── */}
      <Section accentColor="#8a9aab" emoji="ℹ️" label="About">
        <div className="stp-about-card">
          <div className="stp-about-row">
            <span className="stp-about-icon"><Info size={12} /></span>
            <span><strong>LensPro AI Camera</strong> — Version 1.0.0</span>
          </div>
        </div>
      </Section>

    </section>
  );
}
